import React, { useContext, type FC, type PropsWithChildren } from 'react';

import { useSmartlingStrings } from '@lucid/hooks';

import { type Lucid } from '../../types/types';
import Image from '../image/image';
import Box from '../box/box';
import Heading from '../heading/heading';
import Link, { type IconAnimationType } from '../link/link';
import Text from '../text/text';
import { LayoutContext } from '..';

export interface TemplateHeroProps {
  /** Allows the component to get additional styles. */
  className?: string;
  /** The button CTA link and text. */
  cta?: Lucid.Directus.Generated.Links;
  /** show the cta as a link instead of a button */
  willDisplayCtaAsLink?: boolean;
  /** Determines whether the page will add SSO buttons to CTA */
  hasSSO?: boolean;
  /** Icon animation type for cta links */
  iconAnimation?: IconAnimationType;
  /** Adjusts text color for dark backgrounds */
  hasDarkBackground?: boolean;
  /** The heading text. */
  header: string;
  /** The header variants. */
  headerVariant?: Lucid.Theme.Component.Typography.VariantKeys;
  /** The text displayed under the ctas */
  underCtaText?: string;
  /** Scroll options for cta links */
  scrollToOptions?: ScrollIntoViewOptions;
  /** Products template is integrated with */
  product?: Lucid.Product.Sites;
}

const lucidLogos = {
  chart: {
    withText: 'https://corporate-assets.lucid.co/co/120f906d-9405-4960-a68b-ff08a452835a.svg',
    logoOnly: 'https://corporate-assets.lucid.co/co/2e8149c5-2a9f-4d67-9b91-5ae21d2a07d7.png',
  },
  lucidscale: {
    withText: 'https://corporate-assets.lucid.co/co/f3177829-1082-487d-928f-8bc61223b56f.svg',
    logoOnly: 'https://corporate-assets.lucid.co/co/d94bd7f4-8d38-48a0-a561-b26337b6df91.png.png',
  },
  spark: {
    withText: 'https://corporate-assets.lucid.co/co/9af2902e-a123-4e64-abd0-9326a6db3a29.svg',
    logoOnly: 'https://corporate-assets.lucid.co/co/5d061930-cb8b-4b4a-989e-78356e0806c6.png',
  },
};

const TemplateHero: FC<PropsWithChildren<TemplateHeroProps>> = ({
  className,
  cta,
  willDisplayCtaAsLink,
  iconAnimation = 'default',
  hasSSO,
  hasDarkBackground,
  header,
  headerVariant = 'headingXL',
  underCtaText,
  scrollToOptions,
  product,
  ...rest
}) => {
  const { shortCode } = useContext(LayoutContext);
  const withSmartling = useSmartlingStrings({
    keys: ['mktg_template_product_on'],
    shortCode,
  });
  const logoImage =
    product && product in lucidLogos
      ? lucidLogos[product as Exclude<Lucid.Marketplace.LaunchUrlProducts, null>].withText
      : null;

  return (
    <Box
      {...rest}
      css={theme => ({
        label: 'hero',
        margin: `${theme.space.s80}px 0px ${theme.space.s64}px`,
        display: 'flex',
        justifyContent: 'center',
      })}
      // eslint-disable-next-line @stylistic/jsx-sort-props
      className={className}
    >
      <Box
        css={theme => ({
          maxWidth: '980px',
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexDirection: 'column',
          gap: theme.space.s24,
          [theme.breakpoints.bp500.min]: {
            flexDirection: 'row',
            gap: theme.space.s64,
          },
        })}
      >
        <Box>
          <Heading as="h1" dataElementPageLocation="hero" variant={headerVariant}>
            {header}
          </Heading>
          {product && (
            <Box>
              <Text css={theme => ({ color: theme.colors.coolGrey[60] })} variant="labelXS400">
                {withSmartling('mktg_template_product_on')}
              </Text>
              <Box
                css={theme => ({
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: theme.space.s24,
                  [theme.breakpoints.bp768.min]: { flexDirection: 'row', flexWrap: 'wrap' },
                })}
              >
                {logoImage && (
                  <Image
                    key={`${product}-integration-logo`}
                    dataElementPageLocation="hero"
                    height="24px"
                    imageAlt={`Lucid${product} logo`}
                    imageSrc={logoImage}
                  />
                )}
              </Box>
            </Box>
          )}
        </Box>
        <Box
          css={theme => ({
            justifyContent: 'center',
            width: '100%',
            [theme.breakpoints.bp500.min]: {
              width: 'unset',
            },
          })}
        >
          <Box>
            {cta && !hasSSO && (
              <Link
                aria-describedby="heroLinkDescription"
                css={theme => ({
                  width: '100%',
                  [theme.breakpoints.bp500.min]: {
                    width: 'auto',
                  },
                })}
                displayAsButton={!willDisplayCtaAsLink}
                href={cta.final_url}
                iconAnimation={iconAnimation ? iconAnimation : 'navigational'}
                scrollToOptions={scrollToOptions}
                variant={hasDarkBackground ? 'secondary' : 'primary'}
              >
                {cta.default_link_text}
              </Link>
            )}
          </Box>
          {underCtaText && (
            <Text
              css={theme => ({ textAlign: 'center', margin: 0, color: theme.colors.coolGrey[80] })}
              variant="labelXS400"
            >
              {underCtaText}
            </Text>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default TemplateHero;
